import { Document } from "../../../models/Document"
import { FetchErrorInfo, StartFetchingAction, SuccessFetchingAction, ErrorFetchingAction, FetchRequestActions } from "../../../utils/request-actions-helpers/createRequestActions"
import { AddDocumentsAction, ADD_DOCUMENTS } from "./actions/addDocumentsActions"
import { DeleteDocumentActions, deleteDocumentActionTypes, DeleteDocumentActionParameters, DeleteDocumentResponse } from "./actions/deleteDocumentAction"
import { FetchDocumentAdditionalData, fetchDocumentsActionTypes } from "./actions/fetch/fetchDocuments"
import { fetchHiddenDocumentsActionTypes } from "./actions/fetch/fetchHiddenDocuments"
import { UpdateDocumentAdditionalData, DocumentUpdateInfo, DocumentAddRelatedUpdateAdditionalData } from "./actions/updates/UpdateDocumentAdditionalData"
import { updateDocumentMembersActionTypes } from "./actions/updates/updateDocumentMembers"
import { updateDocumentPermissionActionTypes } from "./actions/updates/permissions/updateDocumentPermissions"
import { confirmReadingRequestActionTypes } from "./actions/updates/confirmReading"
import { declineReadingRequestActionTypes } from "./actions/updates/declineReading"
import { DeleteRelatedDocumentAdditionalData, deleteRelatedDocumentActionTypes } from "./actions/updates/relatedDocuments/deleteRelatedDocument"
import { selectAnswerActionTypes } from "./actions/updates/selectAnswer"
import { updateDocumentJsonDataActionTypes } from './actions/updates/updateDocumentJsonData'
import { UpdateDocumentJsonDataAdditionalData } from './actions'
import { updateDocumentAggregationsActionTypes } from './actions/updates/updateDocumentAggregations'
import { updateDocumentRatingActionTypes, updateDocumentTitleActionTypes, addChecklistActionTypes, updateChecklistTitleActionTypes, deleteChecklistActionTypes, updateDocumentArticleWysiwygActionTypes, addFileAttachmentsActionTypes, createArticleActionTypes, updateDocumentLocationActionTypes, updateDocumentScheduleActionTypes, updatePreviewImage, updatePreviewImageActionTypes, deleteFileAttachmentActionTypes, moveDocumentBetweenLanesActionTypes, updateDocumentMustReadActionTypes, updateDocumentBusinessIdActionTypes } from "./actions"
import { addRelatedDocumentActionTypes } from "./actions/updates/relatedDocuments/addRelatedDocument"
import { resetDocumentMustReadActionTypes } from "./actions/updates/resetDocumentMustRead"
import { createCustomInputActionTypes, createCustomInputSilentActionTypes } from "./actions/create/createCustomInput"
import { updateDocumentLabelsActionTypes } from "./actions/updates/updateDocumentLabels"
import { moveDocumentSuccessLaneActionTypes } from "./actions/updates/moveLane/moveDocumentToSuccessLane"
import { DocumentScheduleAction, UPDATE_DOCUMENT_SCHEDULE_LOCALLY } from "./localActions/localActions"
import { LocalDocumentPermissionsRequest } from "../../../models"
import { moveDocumentBetweenGanttLanesActionTypes } from "./actions/updates/moveLane/moveDocumentBetweenGanttLanes"

type SetDocLabels = {type: 'SET_DOCUMENT_LABELS', docId: string, labels: string[]}
type SetDocMembers = {type: 'SET_DOCUMENT_MEMBERS', docId: string, members: string[]}
type SetDocStatus = {type: 'SET_DOCUMENT_STATUS', docId: string, status: LocalDocumentPermissionsRequest}
type SetDocTitle = {type: 'SET_DOCUMENT_TITLE', docId: string, title: string}
type SetDocDeps = {type: 'SET_DOCUMENT_DEPENDENCIES', docId: string, dependencies: string[]}
type SetDueDate = {type: 'SET_DOCUMENT_DUEDATE', docId: string, dueDateUTC: any }

export interface DocumentData {
    document?: Document
    isFetching: boolean
    isUpdating: boolean
    updateInfo?: DocumentUpdateInfo
    isDeleting: boolean
    fetchError?: FetchErrorInfo
    updateError?: FetchErrorInfo
    deleteError?: FetchErrorInfo
}

export interface DocIdToDocument {
    [docId: string]: DocumentData | undefined
}

export interface DocumentsState {
    data: DocIdToDocument
}

export type DocumentAction = AddDocumentsAction | DeleteDocumentActions
    | FetchRequestActions<Document[], FetchDocumentAdditionalData>
    | FetchRequestActions<Document, UpdateDocumentAdditionalData>
    | SetDocLabels

export const initialDocumentsState: DocumentsState = {
    data: {}
}

export const documentsReducer = (state: DocumentsState = initialDocumentsState, action: DocumentAction): DocumentsState => {
    switch (action.type) {
        case ADD_DOCUMENTS: {
            const addDocumentsAction = action as AddDocumentsAction
            return {
                data: {
                    ...state.data,
                    ...addDocumentsAction.data
                }
            }
        }
        case fetchDocumentsActionTypes.start: {
            const startFetchingDocumentsAction = action as StartFetchingAction<FetchDocumentAdditionalData>
            if (!startFetchingDocumentsAction.additionalData) {
                return state
            }
            const newData: DocIdToDocument = {}
            startFetchingDocumentsAction.additionalData.docIds.forEach(x => {
                const documentData = state.data[x]

                newData[x] = {
                    document: documentData && documentData.document,
                    isFetching: true,
                    isDeleting: false,
                    isUpdating: false
                }
            })
            return {
                data: {
                    ...state.data,
                    ...newData
                }
            }
        }
        case fetchHiddenDocumentsActionTypes.start: {
            const startFetchingDocumentsAction = action as StartFetchingAction<FetchDocumentAdditionalData>
            if (!startFetchingDocumentsAction.additionalData) {
                return state
            }
            const newData: DocIdToDocument = {}
            startFetchingDocumentsAction.additionalData.docIds.forEach(x => {
                const documentData = state.data[x]

                newData[x] = {
                    document: documentData && documentData.document,
                    isFetching: false,
                    isDeleting: false,
                    isUpdating: false
                }
            })
            return {
                data: {
                    ...state.data,
                    ...newData
                }
            }
        }
        case fetchDocumentsActionTypes.success:
        case fetchHiddenDocumentsActionTypes.success: {
            const successFetchingDocumentsAction = action as SuccessFetchingAction<Document[], FetchDocumentAdditionalData>
            const newData: DocIdToDocument = {}
            const shouldIncrementSeen = !!successFetchingDocumentsAction.additionalData?.logClick && successFetchingDocumentsAction.additionalData?.docIds?.length === 1
            const seenUserUid = successFetchingDocumentsAction?.additionalData?.userUid ?? ''
            const shouldAddSeenUserUid = !!seenUserUid

            if (successFetchingDocumentsAction.result.length === 0) {
                successFetchingDocumentsAction.additionalData?.docIds.forEach(x => {
                    newData[x] = {
                        isDeleting: false,
                        isFetching: false,
                        isUpdating: false,
                    }
                })
            } else {
                successFetchingDocumentsAction.result.forEach(x => {
                    let seenUsers = x?.seenUsers ?? []
                    if(shouldAddSeenUserUid && !seenUsers?.includes?.(seenUserUid)) seenUsers = [...seenUsers, seenUserUid]
                    newData[x.docId] = {
                        isDeleting: false,
                        isFetching: false,
                        isUpdating: false,
                        document: shouldIncrementSeen ? {...x, seenUsers, seenCounter: (x.seenCounter || 0) + 1} : x
                    }
                })
            }


            return {
                data: {
                    ...state.data,
                    ...newData
                }
            }
        }
        case fetchDocumentsActionTypes.error:
        case fetchHiddenDocumentsActionTypes.error: {
            const errorFetchingDocumentsAction = action as ErrorFetchingAction<FetchDocumentAdditionalData>
            if (!errorFetchingDocumentsAction.additionalData) {
                return state
            }
            const newData: DocIdToDocument = {}
            errorFetchingDocumentsAction.additionalData.docIds.forEach(x => {
                newData[x] = {
                    isDeleting: false,
                    isFetching: false,
                    isUpdating: false,
                    fetchError: errorFetchingDocumentsAction.error
                }
            })

            return {
                data: {
                    ...state.data,
                    ...newData
                }
            }
        }
        case createCustomInputActionTypes.success:
        case createCustomInputSilentActionTypes.success:
        case createArticleActionTypes.success: {
            const { result } = action as SuccessFetchingAction<Document>

            return {
                data: {
                    ...state.data,
                    [result.docId]: {
                        isDeleting: false,
                        isFetching: false,
                        isUpdating: false,
                        document: result
                    }
                }
            }
        }
        case deleteChecklistActionTypes.start:
        case updateChecklistTitleActionTypes.start:
        case addChecklistActionTypes.start:
        case selectAnswerActionTypes.start:
        case updateDocumentPermissionActionTypes.start:
        case moveDocumentBetweenLanesActionTypes.start:
        case moveDocumentBetweenGanttLanesActionTypes.start:
        case moveDocumentBetweenLanesActionTypes.startWithoutSnackbar:
        case moveDocumentBetweenGanttLanesActionTypes.startWithoutSnackbar:
        case moveDocumentSuccessLaneActionTypes.start:
        case updateDocumentMembersActionTypes.start:
        case updateDocumentMustReadActionTypes.start:
        case updateDocumentJsonDataActionTypes.start:
        case resetDocumentMustReadActionTypes.start:
        case updateDocumentAggregationsActionTypes.start:
        case updateDocumentTitleActionTypes.start:
        case updateDocumentRatingActionTypes.start:
        case updateDocumentBusinessIdActionTypes.start:
        case updateDocumentLocationActionTypes.start:
        case updatePreviewImageActionTypes.start:
        case updateDocumentScheduleActionTypes.start:
        case updateDocumentArticleWysiwygActionTypes.start:
        case updateDocumentLabelsActionTypes.start:
        case addRelatedDocumentActionTypes.start:
        case addFileAttachmentsActionTypes.start:
        case deleteFileAttachmentActionTypes.start:
        case declineReadingRequestActionTypes.start:
        case confirmReadingRequestActionTypes.start: {
            const startUpdating = action as StartFetchingAction<UpdateDocumentAdditionalData>
            if (!startUpdating.additionalData) {
                return state
            }
            const newState: DocumentsState = {
                data: {
                    ...state.data
                }
            }
            const docId = startUpdating.additionalData.docId
            const documentBeingUpdated = newState.data[docId]
            if (!documentBeingUpdated) {
                return state
            } else {
                return {
                    data: {
                        ...state.data,
                        [docId]: {
                            ...documentBeingUpdated,
                            isUpdating: true,
                            updateInfo: startUpdating.additionalData.updateInfo
                        }
                    }
                }
            }
        }
        case deleteChecklistActionTypes.success:
        case updateChecklistTitleActionTypes.success:
        case addChecklistActionTypes.success:
        case selectAnswerActionTypes.success:
        case updateDocumentPermissionActionTypes.success:
        case updateDocumentLabelsActionTypes.success:
        case moveDocumentBetweenLanesActionTypes.success:
        case moveDocumentBetweenLanesActionTypes.successWithoutSnackbar:
        case moveDocumentBetweenGanttLanesActionTypes.success:
        case moveDocumentBetweenGanttLanesActionTypes.successWithoutSnackbar:
        case moveDocumentSuccessLaneActionTypes.success:
        case updateDocumentMembersActionTypes.success:
        case updateDocumentMustReadActionTypes.success:
        case updateDocumentAggregationsActionTypes.success:
        case updateDocumentArticleWysiwygActionTypes.success:
        case updateDocumentTitleActionTypes.success:
        case updateDocumentRatingActionTypes.success:
        case updateDocumentLocationActionTypes.success:
        case updatePreviewImageActionTypes.success:
        case resetDocumentMustReadActionTypes.success:
        case updateDocumentScheduleActionTypes.success:
        case addFileAttachmentsActionTypes.success:
        case deleteFileAttachmentActionTypes.success:
        case declineReadingRequestActionTypes.success:
        case declineReadingRequestActionTypes.success:
        case confirmReadingRequestActionTypes.success: {
            const successUpdatingAction = action as SuccessFetchingAction<Document, UpdateDocumentAdditionalData>
            if (!successUpdatingAction.additionalData) {
                return state
            }
            return {
                data: {
                    ...state.data,
                    [successUpdatingAction.additionalData.docId]: {
                        isUpdating: false,
                        isDeleting: false,
                        isFetching: false,
                        document: successUpdatingAction.result
                    }
                }
            }
        }
        case updateDocumentBusinessIdActionTypes.success:
        case updateDocumentJsonDataActionTypes.success: {
            const successUpdatingAction = action as SuccessFetchingAction<Document, UpdateDocumentJsonDataAdditionalData>
            if (!successUpdatingAction.additionalData) {
                return state
            }

            const doc = successUpdatingAction.result

            if(!!successUpdatingAction.additionalData?.ignoreResultTitle && state.data[successUpdatingAction.additionalData.docId]?.document) {
                doc.title = state.data[successUpdatingAction.additionalData.docId]?.document?.title ?? doc.title
            }

            return {
                data: {
                    ...state.data,
                    [successUpdatingAction.additionalData.docId]: {
                        isUpdating: false,
                        isDeleting: false,
                        isFetching: false,
                        document: doc
                    }
                }
            }
        }
        case 'SET_DOCUMENT_LABELS': {
            const updatingAction = action as SetDocLabels
            const doc = state.data[updatingAction.docId]
            if(!doc || !doc.document || !Object.keys(doc.document).length) return state
            return {
                data: {
                    ...state.data,
                    [updatingAction.docId]: {
                        ...doc,
                        document: {...doc.document, collectionLabels: updatingAction.labels}
                    }
                }
            }
        }
        case 'SET_DOCUMENT_MEMBERS': {
            const updatingAction = action as SetDocMembers
            const doc = state.data[updatingAction.docId]
            if(!doc || !doc.document || !Object.keys(doc.document).length) return state
            return {
                data: {
                    ...state.data,
                    [updatingAction.docId]: {
                        ...doc,
                        document: {...doc.document, members: updatingAction.members.map(id => ({userUUID: id}))}
                    }
                }
            }
        }
        case 'SET_DOCUMENT_DEPENDENCIES': {
            const updatingAction = action as SetDocDeps
            const doc = state.data[updatingAction.docId]
            if(!doc || !doc.document || !Object.keys(doc.document).length) return state
            return {
                data: {
                    ...state.data,
                    [updatingAction.docId]: {
                        ...doc,
                        document: {...doc.document, dependencies: updatingAction.dependencies}
                    }
                }
            }
        }
        case 'SET_DOCUMENT_STATUS': {
            const updatingAction = action as SetDocStatus
            const doc = state.data[updatingAction.docId]
            if(!doc || !doc.document || !Object.keys(doc.document).length) return state
            return {
                data: {
                    ...state.data,
                    [updatingAction.docId]: {
                        ...doc,
                        document: {
                            ...doc.document, 
                            ...updatingAction.status,
                        }
                    }
                }
            }
        }
        case 'SET_DOCUMENT_TITLE': {
            const updatingAction = action as SetDocTitle
            const doc = state.data[updatingAction.docId]
            if(!doc || !doc.document || !Object.keys(doc.document).length) return state
            return {
                data: {
                    ...state.data,
                    [updatingAction.docId]: {
                        ...doc,
                        document: {
                            ...doc.document, 
                            title: updatingAction.title
                        }
                    }
                }
            }
        }
        case UPDATE_DOCUMENT_SCHEDULE_LOCALLY: {
            const updatingAction = action as any
            const doc = state.data[updatingAction.docId]
            if(!doc || !doc.document || !Object.keys(doc.document).length) return state
            return {
                data: {
                    ...state.data,
                    [updatingAction.docId]: {
                        ...doc,
                        document: {
                            ...doc.document, 
                            dueDateUTC: updatingAction.payload.dueDateUTC,
                            dueDateRecurring: updatingAction.payload.dueDateRecurring,
                            dateTimeFrom: updatingAction.payload.dateTimeFrom,
                            dateTimeTo: updatingAction.payload.dateTimeTo,
                            taskDueDateDone: updatingAction.payload.taskDueDateDone,
                        }
                    }
                }
            }
        }
        case addRelatedDocumentActionTypes.success: {
            const successUpdatingAction = action as SuccessFetchingAction<Document, DocumentAddRelatedUpdateAdditionalData>
            if (!successUpdatingAction.additionalData || !successUpdatingAction.additionalData.updateInfo) {
                return state
            }
            const newState = {
                data: {
                    ...state.data,
                    [successUpdatingAction.additionalData.docId]: {
                        isUpdating: false,
                        isDeleting: false,
                        isFetching: false,
                        document: successUpdatingAction.result
                    }
                }
            }
            // const {linkId, linkedDocument} = successUpdatingAction.additionalData.updateInfo

            // const {docId: needsLinkDocId, relatedDocuments: originalRelated, title, dataType: needsLinkDataType } = successUpdatingAction.result
            // const doc = state.data[linkId]
            
            // if(!doc || !doc.document || !linkedDocument) return newState

            // const {relatedDocuments} = doc.document
            // const relatedLink = originalRelated.find(r => r.relatedDocumentId === linkId)
            
            // if(!!relatedLink && (!relatedDocuments.length || !relatedDocuments.find(d => d.relatedDocumentId === needsLinkDocId))) {
            //     const newLink = {...relatedLink, title, relatedDocumentId: needsLinkDocId, dataType: needsLinkDataType}
                
            //     newState.data[linkId] = {
            //         ...doc, 
            //         document: {...doc.document, relatedDocuments: [...relatedDocuments, newLink]}
            //     }
            // }
            return newState
        }

        case deleteChecklistActionTypes.error:
        case updateChecklistTitleActionTypes.error:
        case addChecklistActionTypes.error:
        case selectAnswerActionTypes.error:
        case moveDocumentBetweenLanesActionTypes.error:
        case moveDocumentBetweenGanttLanesActionTypes.error:
        case moveDocumentSuccessLaneActionTypes.error:
        case updateDocumentPermissionActionTypes.error:
        case updateDocumentMembersActionTypes.error:
        case updateDocumentMustReadActionTypes.error:
        case updateDocumentJsonDataActionTypes.error:
        case updateDocumentArticleWysiwygActionTypes.error:
        case updateDocumentAggregationsActionTypes.error:
        case updateDocumentTitleActionTypes.error:
        case updateDocumentRatingActionTypes.error:
        case updateDocumentLabelsActionTypes.error:
        case updateDocumentBusinessIdActionTypes.error:
        case updatePreviewImageActionTypes.error:
        case updateDocumentLocationActionTypes.error:
        case updateDocumentScheduleActionTypes.error:
        case resetDocumentMustReadActionTypes.error:
        case addRelatedDocumentActionTypes.error:
        case addFileAttachmentsActionTypes.error:
        case deleteFileAttachmentActionTypes.error:
        case declineReadingRequestActionTypes.error:
        case confirmReadingRequestActionTypes.error: {
            const errorUpdatingAction = action as ErrorFetchingAction<UpdateDocumentAdditionalData>
            if (!errorUpdatingAction.additionalData) {
                return state
            }
            const documentFailedToUpdate = state.data[errorUpdatingAction.additionalData.docId]
            if (!documentFailedToUpdate) {
                return state
            }
            return {
                data: {
                    ...state.data,
                    [errorUpdatingAction.additionalData.docId]: {
                        isUpdating: false,
                        isDeleting: false,
                        isFetching: false,
                        document: documentFailedToUpdate.document,
                        updateError: errorUpdatingAction.error
                    }
                }
            }
        }
        case updateDocumentBusinessIdActionTypes.error:  {
            const errorUpdatingAction = action as ErrorFetchingAction<UpdateDocumentAdditionalData>
            
            if (!errorUpdatingAction.additionalData) {
                return state
            }
            const documentFailedToUpdate = state.data[errorUpdatingAction.additionalData.docId]
            if (!documentFailedToUpdate) {
                return state
            }
            return {
                data: {
                    ...state.data,
                    [errorUpdatingAction.additionalData.docId]: {
                        isUpdating: false,
                        isDeleting: false,
                        isFetching: false,
                        document: documentFailedToUpdate.document,
                        updateError: errorUpdatingAction.error
                    }
                }
            }
    }

        case deleteDocumentActionTypes.start: {
            const deleteAction = action as StartFetchingAction<DeleteDocumentActionParameters>
            if (!deleteAction.additionalData) {
                return state
            }
            const documentData = state.data[deleteAction.additionalData.docId]
            if (!documentData) {
                return state
            }
            return {
                data: {
                    ...state.data,
                    [deleteAction.additionalData.docId]: {
                        ...documentData,
                        isDeleting: true
                    }
                }
            }
        }
        case deleteDocumentActionTypes.success: {
            const deleteAction = action as SuccessFetchingAction<DeleteDocumentResponse, DeleteDocumentActionParameters>
            if (!deleteAction.additionalData) {
                return state
            }
            const newData = {
                ...state.data
            }
            delete newData[deleteAction.additionalData.docId]

            return {
                data: newData
            }
        }
        case deleteDocumentActionTypes.error: {
            const deleteAction = action as ErrorFetchingAction<DeleteDocumentActionParameters>
            if (!deleteAction.additionalData) {
                return state
            }
            const documentData = state.data[deleteAction.additionalData.docId]
            if (!documentData) {
                return state
            }
            return {
                data: {
                    ...state.data,
                    [deleteAction.additionalData.docId]: {
                        ...documentData,
                        isDeleting: false,
                        deleteError: deleteAction.error
                    }
                }
            }
        }

        case deleteRelatedDocumentActionTypes.start: {
            const startDeleting = action as StartFetchingAction<DeleteRelatedDocumentAdditionalData>
            if (!startDeleting.additionalData) {
                return state
            }
            const documentsBeingUpdated: DocIdToDocument = {}
            startDeleting.additionalData.docIds.forEach(docId => {
                const documentData = state.data[docId]
                if (documentData) {
                    documentsBeingUpdated[docId] = {
                        isDeleting: false,
                        isFetching: false,
                        isUpdating: true,
                        document: documentData.document
                    }
                }
            })
            return {
                data: {
                    ...state.data,
                    ...documentsBeingUpdated
                }
            }
        }

        case deleteRelatedDocumentActionTypes.success: {
            const successDeleting = action as SuccessFetchingAction<Document[], DeleteRelatedDocumentAdditionalData>
            if (!successDeleting.additionalData) {
                return state
            }

            const updatedDocuments: DocIdToDocument = {}
            successDeleting.additionalData.docIds.forEach(docId => {
                const updatedDocument = successDeleting.result.find(x => x.docId === docId)
                updatedDocuments[docId] = {
                    isDeleting: false,
                    isFetching: false,
                    isUpdating: false,
                    document: updatedDocument
                }
            })

            return {
                data: {
                    ...state.data,
                    ...updatedDocuments
                }
            }
        }

        case deleteRelatedDocumentActionTypes.error: {
            const errorDeleting = action as ErrorFetchingAction<DeleteRelatedDocumentAdditionalData>
            if (!errorDeleting.additionalData) {
                return state
            }

            const updatedDocuments: DocIdToDocument = {}
            errorDeleting.additionalData.docIds.forEach(docId => {
                const documentData = state.data[docId]
                if (documentData) {
                    updatedDocuments[docId] = {
                        isDeleting: false,
                        isFetching: false,
                        isUpdating: false,
                        document: documentData.document,
                        updateError: errorDeleting.error
                    }
                }
            })

            return {
                data: {
                    ...state.data,
                    ...updatedDocuments
                }
            }
        }

        default:
            return state
    }
}